import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { ChevronDown, Settings, HelpCircle, LogOut, Shield } from 'lucide-react';
import { Avatar } from './Avatar';
import { useAdmin } from '../hooks/useAdmin';

const navLinks = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'Expenses', to: '/expenses' },
  { label: 'Documents', to: '/documents' },
  { label: 'Export', to: '/export' },
];

export function Navigation() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const isAdmin = useAdmin();

  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on Escape and return focus to avatar button
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        avatarButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDropdownOpen]);

  // Move focus to first menu item when dropdown opens
  useEffect(() => {
    if (!isDropdownOpen) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [isDropdownOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E8F0] z-40">
      <div className="h-full flex items-center justify-between px-6">
        {/* Logo */}
        <div className="flex items-center gap-12">
          <div className="flex items-center gap-1">
            <span className="text-[16px] font-semibold text-[#1B365D]">STEADFAST</span>
            <span className="text-[16px] font-semibold text-[#C9A227]">HSA</span>
          </div>

          {/* Navigation Links */}
          <div className="flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.label}
                  to={link.to}
                  aria-current={isActive ? 'page' : undefined}
                  className={`
                    text-[14px] font-medium relative pb-1 transition-colors
                    ${isActive
                      ? 'text-[#1B365D]'
                      : 'text-[#4A5568] hover:text-[#1B365D]'
                    }
                  `}
                >
                  {link.label}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1B365D]" />
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Avatar with Dropdown */}
        <div className="relative">
          <button
            ref={avatarButtonRef}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-haspopup="menu"
            aria-expanded={isDropdownOpen}
            aria-label="Account menu"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Avatar
              size={36}
              src={user?.imageUrl}
              initials={user?.firstName?.[0] + (user?.lastName?.[0] || '')}
            />
            <ChevronDown size={16} className="text-[#4A5568]" />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsDropdownOpen(false)}
              />
              <div 
                ref={menuRef}
                role="menu"
                className="absolute top-full right-0 mt-2 w-[200px] bg-white rounded-lg p-1 z-50"
                style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
              >
                {/* User Info Header */}
                <div className="px-3 py-3 border-b border-[#E2E8F0]">
                  <div className="text-[14px] font-semibold text-[#1B365D]">
                    {user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                  </div>
                  <div className="text-[12px] text-[#718096]">
                    {user?.primaryEmailAddress?.emailAddress || ''}
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <Link
                    role="menuitem"
                    to="/settings"
                    onClick={() => setIsDropdownOpen(false)}
                    className="w-full flex items-center gap-3 h-10 px-3 text-[14px] text-[#4A5568] hover:bg-[#F7FAFC] rounded transition-colors"
                  >
                    <Settings size={16} />
                    <span>Settings</span>
                  </Link>
                  <Link
                    role="menuitem"
                    to="/accounts"
                    onClick={() => setIsDropdownOpen(false)}
                    className="w-full flex items-center gap-3 h-10 px-3 text-[14px] text-[#4A5568] hover:bg-[#F7FAFC] rounded transition-colors"
                  >
                    <Settings size={16} />
                    <span>Accounts</span>
                  </Link>
                  {isAdmin && (
                    <Link
                      role="menuitem"
                      to="/admin"
                      onClick={() => setIsDropdownOpen(false)}
                      className="w-full flex items-center gap-3 h-10 px-3 text-[14px] text-[#C9A227] hover:bg-[#F7FAFC] rounded transition-colors"
                    >
                      <Shield size={16} />
                      <span>Admin</span>
                    </Link>
                  )}
                  <button
                    role="menuitem"
                    onClick={() => setIsDropdownOpen(false)}
                    className="w-full flex items-center gap-3 h-10 px-3 text-[14px] text-[#4A5568] hover:bg-[#F7FAFC] rounded transition-colors"
                  >
                    <HelpCircle size={16} />
                    <span>Help & Support</span>
                  </button>
                </div>

                <div className="border-t border-[#E2E8F0] pt-1">
                  <button
                    role="menuitem"
                    onClick={() => signOut()}
                    className="w-full flex items-center gap-3 h-10 px-3 text-[14px] text-[#DC2626] hover:bg-[#F7FAFC] rounded transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}